import { motion } from "framer-motion";

const appFeatures = [
  {
    title: "Android App Development",
    description:
      "Custom Android applications with modern UI, fast performance, and scalable architecture.",
    icon: "🤖",
  },
  {
    title: "iOS App Development",
    description:
      "Premium iOS applications built for performance, security, and elegant user experiences.",
    icon: "🍎",
  },
  {
    title: "UI/UX Design",
    description:
      "Beautiful and user-friendly mobile interfaces designed for higher engagement.",
    icon: "🎨",
  },
  {
    title: "API Integration",
    description:
      "Secure API integrations including payments, authentication, maps, chat systems, and more.",
    icon: "🔗",
  },
];

const process = [
  "Requirement Analysis",
  "UI/UX Design",
  "App Development",
  "Testing & QA",
  "Play Store / App Store Deployment",
];

const MobileAppDevelopments = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 px-6 py-28 text-white">

      {/* Background Glow Effects */}
      <div className="absolute left-10 top-32 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl"></div>

      <div className="absolute bottom-20 right-10 h-96 w-96 rounded-full bg-cyan-500/20 blur-3xl"></div>

      <div className="mx-auto max-w-7xl">

        {/* HERO SECTION */}
        <div className="grid items-center gap-20 md:grid-cols-2">

          {/* LEFT CONTENT */}
          <motion.div
            initial={{ opacity: 0, x: -80 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl font-black leading-tight md:text-7xl">
              Android & iOS
              <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                {" "}App Development
              </span>
            </h1>

            <p className="mt-8 text-lg leading-8 text-slate-300">
              We build powerful mobile applications for startups,
              businesses, and enterprises with premium UI/UX,
              fast performance, and scalable backend systems.
            </p>

            <div className="mt-10 flex flex-wrap gap-5">

              <button className="rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 px-8 py-4 font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(79,70,229,0.5)]">
                Start Your Project
              </button>

              <button className="rounded-2xl border border-white/20 bg-white/10 px-8 py-4 font-bold text-white backdrop-blur-lg transition-all duration-300 hover:bg-white/20">
                View Portfolio
              </button>

            </div>

            {/* STATS */}
            <div className="mt-16 grid grid-cols-3 gap-6">

              <div className="rounded-3xl border border-white/10 bg-white/10 p-6 text-center backdrop-blur-xl">
                <h2 className="text-4xl font-black text-cyan-400">50+</h2>
                <p className="mt-2 text-slate-300">Apps Built</p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/10 p-6 text-center backdrop-blur-xl">
                <h2 className="text-4xl font-black text-indigo-400">99%</h2>
                <p className="mt-2 text-slate-300">Client Satisfaction</p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/10 p-6 text-center backdrop-blur-xl">
                <h2 className="text-4xl font-black text-cyan-400">24/7</h2>
                <p className="mt-2 text-slate-300">Support</p>
              </div>

            </div>
          </motion.div>

          {/* RIGHT MOBILE MOCKUP */}
          <motion.div
            animate={{
              y: [0, -20, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
            }}
            className="mx-auto"
          >
            <div className="relative h-[650px] w-[320px] rounded-[50px] border border-white/10 bg-white/10 p-4 shadow-2xl backdrop-blur-xl">

              <div className="h-full w-full rounded-[40px] bg-gradient-to-br from-indigo-500 via-cyan-500 to-slate-900 p-6">

                <div className="rounded-3xl bg-white/10 p-5 backdrop-blur-xl">
                  <h2 className="text-2xl font-bold">
                    ASIDUO SOLUTIONS
                  </h2>

                  <p className="mt-3 text-sm text-slate-200">
                    Premium Mobile Experiences
                  </p>
                </div>

                <div className="mt-8 space-y-5">

                  <div className="rounded-2xl bg-white/10 p-5 backdrop-blur-xl">
                    <h3 className="font-bold">App Analytics</h3>

                    <div className="mt-4 h-3 rounded-full bg-white/10">
                      <div className="h-full w-[85%] rounded-full bg-white"></div>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white/10 p-5 backdrop-blur-xl">
                    <h3 className="font-bold">Performance</h3>

                    <div className="mt-4 h-3 rounded-full bg-white/10">
                      <div className="h-full w-[92%] rounded-full bg-cyan-300"></div>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white/10 p-5 backdrop-blur-xl">
                    <h3 className="font-bold">Security</h3>

                    <div className="mt-4 h-3 rounded-full bg-white/10">
                      <div className="h-full w-[96%] rounded-full bg-indigo-300"></div>
                    </div>
                  </div>

                </div>

              </div>

            </div>
          </motion.div>

        </div>

        {/* FEATURES SECTION */}
        <div className="mt-32">

          <h2 className="text-center text-4xl font-black md:text-6xl">
            Our App Development Services
          </h2>

          <div className="mt-20 grid gap-10 md:grid-cols-2 lg:grid-cols-4">

            {appFeatures.map((feature, index) => (
              <motion.div
                key={index}
                whileHover={{
                  scale: 1.05,
                  y: -10,
                }}
                className="rounded-3xl border border-white/10 bg-white/10 p-8 backdrop-blur-xl transition-all duration-500"
              >
                <div className="text-6xl">{feature.icon}</div>

                <h3 className="mt-6 text-2xl font-bold">
                  {feature.title}
                </h3>

                <p className="mt-5 leading-7 text-slate-300">
                  {feature.description}
                </p>
              </motion.div>
            ))}

          </div>

        </div>

        {/* DEVELOPMENT PROCESS */}
        <div className="mt-32">

          <h2 className="text-center text-4xl font-black md:text-6xl">
            Our Development Process
          </h2>

          <div className="mt-20 grid gap-8 md:grid-cols-5">

            {process.map((step, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="rounded-3xl border border-white/10 bg-white/10 p-8 text-center backdrop-blur-xl"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 to-cyan-600 text-2xl font-black">
                  {index + 1}
                </div>

                <h3 className="mt-6 text-xl font-bold">
                  {step}
                </h3>
              </motion.div>
            ))}

          </div>

        </div>

        {/* CTA SECTION */}
        <div className="mt-32 rounded-[40px] border border-white/10 bg-white/10 p-16 text-center backdrop-blur-xl">

          <h2 className="text-4xl font-black md:text-6xl">
            Ready to Build Your App?
          </h2>

          <p className="mx-auto mt-8 max-w-3xl text-lg leading-8 text-slate-300">
            Launch your next mobile application with premium design,
            scalable backend systems, and enterprise-level performance.
          </p>

          <button className="mt-10 rounded-2xl bg-gradient-to-r from-indigo-600 to-cyan-600 px-10 py-5 text-lg font-bold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(79,70,229,0.5)]">
            Contact Us
          </button>

        </div>

      </div>
    </div>
  );
};

export default MobileAppDevelopments;